import { FunctionComponent } from "react";

import { confirm, UseFormMethods } from "@bps/fluent-ui";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  RestrictionType,
  TenantDefaultUsersAuthenticationDto,
  UserDefaultAuthenticationDto,
  UserNetworkAuthenticationSettingsDto
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useAddUserDefaultAuth,
  useUpdateUserDefaultAuth
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import {
  GlobalNetworkRestrictionsSection,
  GlobalNetworkRestrictionsValues
} from "../../../_shared-blocks/network-authention-settings-section/GlobalNetworkRestrictionsSection";

interface UserGlobalNetworkRestrictionsSectionProps {
  tenantAuthSettings: TenantDefaultUsersAuthenticationDto | undefined;
  authSettings: UserDefaultAuthenticationDto | undefined;
  tenantId: string;
  userId: string;
  networkSettings: UserNetworkAuthenticationSettingsDto[];
}

export const UserGlobalNetworkRestrictionsSection: FunctionComponent<UserGlobalNetworkRestrictionsSectionProps> = ({
  tenantId,
  userId,
  tenantAuthSettings,
  authSettings,
  networkSettings
}) => {
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltCatalogUserAuthenticationWrite
  );

  const { mutateAsync: addTenantAuth } = useAddUserDefaultAuth();
  const { mutateAsync: updateTenantAuth } = useUpdateUserDefaultAuth();

  const onSubmit = async (
    values: GlobalNetworkRestrictionsValues,
    actions: Pick<
      UseFormMethods<GlobalNetworkRestrictionsValues>,
      "reset" | "setValue"
    >
  ) => {
    // Show a warning message if the restriction type is "IP" and no enabled IP range is specified.
    if (
      values.restrictionType === RestrictionType.IP &&
      !networkSettings.some(ip => ip.isActive)
    ) {
      const isConfirmed = await confirm({
        confirmButtonProps: {
          text: "Ok"
        },
        cancelButtonProps: {
          text: "Cancel"
        },
        dialogContentProps: {
          title: "No Enabled IP Range?",
          subText:
            "You have not specified an enabled IP range - this will block all access. Are you sure you want to continue?"
        }
      });
      if (!isConfirmed) return;
    }

    if (values.restrictionType === RestrictionType.Default) {
      values.restrictionType = undefined;
    }

    if (authSettings) {
      await updateTenantAuth({ ...authSettings, ...values });
    } else {
      await addTenantAuth({ ...values, userId, tenantId });
    }

    if (values.restrictionType === undefined) {
      values.restrictionType = RestrictionType.Default;
    }
    actions.reset(values);
  };

  return (
    <GlobalNetworkRestrictionsSection
      hasWritePermission={!!hasWritePermission}
      initialValues={{
        isUser: true,
        tenantRestrictionType:
          tenantAuthSettings?.restrictionType ?? RestrictionType.Worldwide,
        restrictionType:
          authSettings?.restrictionType ?? RestrictionType.Default
      }}
      onSubmit={onSubmit}
    />
  );
};
